import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {map, throttleTime} from 'rxjs/operators';
import {max, min, numUtil, plus, minus, gt, gte, toString, times, toFixed} from '@/helper/num';
import {contracts$, currenciesCurrencyCache$, coinMarginedContractGray$, riskEngineMode$} from '@/config';
import {positions$} from '@/positions/positions';
import {type InnerContractAccount, innerContractAccounts$} from './inner-contract-accounts';
import type {CurrencyEntity, Position, ContractPair, ContractAccount} from '@/types';
import {contractUtil} from '@/helper/contract';

export const contractAccounts$ = combineLatest([
  innerContractAccounts$,
  positions$,
  currenciesCurrencyCache$,
  contracts$,
  riskEngineMode$,
  coinMarginedContractGray$,
]).pipe(
  throttleTime(100),
  map(([accounts, positions, currencies, contracts, riskEngineMode, coinMarginedContractGray]) => {
    return accounts.data
      .toArray()
      .map((account) => {
        return convertAccount(
          account,
          positions,
          currencies,
          contracts,
          riskEngineMode,
          coinMarginedContractGray,
          accounts.totalCollateralAvaiValueRv
        );
      })
      .filter(Boolean);
  })
);

function calcPositionMarginEv(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.marginEv), '0');
}

function calcUnrealisedPnlEv(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.unrealisedPnlEvByMarkPrice), '0');
}

function calcUnrealisedPnlEv2(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.unrealisedPnlEvByMarkPrice2), '0');
}

function calcFreezeUnrealisedPnlEv(positions: Position[]) {
  return positions
    .filter((o) => o.isCross && o.unrealisedPnlEvByMarkPrice < 0)
    .reduce((p, c) => p + c.unrealisedPnlEvByMarkPrice, 0);
}

function calcOrderMarginEv(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.orderCostEv), '0');
}

export function convertAccount(
  account: Partial<InnerContractAccount>,
  positions: Position[],
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  contracts: ContractPair[],
  riskEngineMode: Boolean,
  coinMarginedContractGray: Boolean = true,
  totalCollateralAvaiValueRv: string | number = 0 // 联合保证金模式，总剩余的可抵押金额
): ContractAccount {
  const {
    currency,
    accountBalanceEv,
    totalUsedBalanceEv,
    bonusBalanceEv,
    isolatedTotalBalanceEv = 0,
    totalDebtEv: borrowEv = '0',
    totalInterestEv: interestEv = '0',
    frozenAsCollateralEv: frozenAsCollateralEv0 = '0', // 可抵押时，抵押数据
    occupationBalanceEv = '0', // 可借时，支持抵押开仓，超出本金占用的金额
    canBorrow = false,
    indexPriceRp = '0',
  } = account;
  const relatedContracts = contracts.filter(({settleCurrency}) => settleCurrency === currency);
  const relatedPositions = positions.filter((o) => relatedContracts.some(({symbol}) => symbol === o.symbol));
  const relatedCrossPositions = positions.filter(({isCross, size, symbol, currency: currency0}) => {
    if (!isCross || size <= 0 || !relatedContracts.some((x) => x.symbol === symbol)) {
      return false;
    }
    if (['USDT', 'USDC'].includes(currency) && riskEngineMode) {
      return ['USDT', 'USDC', 'MUSDT'].includes(currency0);
    }
    return coinMarginedContractGray ? currency0 === currency : false;
  });
  const unrealisedPnlEv = calcUnrealisedPnlEv(relatedPositions);
  if (!currenciesCurrencyCache.has(currency)) {
    return null;
  }
  const {
    valueScale: scale,
    valueFactor: valueFactor0,
    valuePrecision,
    name,
    displayCurrency,
  } = currenciesCurrencyCache.get(currency);
  const availableBalanceValuePrecision = /^USD\w$/.test(currency) ? 4 : valuePrecision;
  const valueScale = contractUtil.formatterValueScale(currency, scale);
  const valueFactor = contractUtil.formatterValueFactor(currency, valueFactor0);
  const valuePrecision2 = contractUtil.formatterValuePrecision(currency, valuePrecision);
  const frozenAsCollateralEv = toFixed(frozenAsCollateralEv0 || 0, valuePrecision2, 3);
  const debtEv = plus(borrowEv, interestEv);
  const relatedCrossUnPnlEv = calcUnrealisedPnlEv2(relatedCrossPositions);
  const coefficient = gt(relatedCrossUnPnlEv, 0) ? 0.99 : 1.01;
  const discountRiskUnPnlEv = Big(relatedCrossUnPnlEv).times(coefficient);
  const availableAccountBalanceEv = Big(accountBalanceEv)
    .minus(isolatedTotalBalanceEv)
    .minus(totalUsedBalanceEv)
    .minus(debtEv)
    .minus(frozenAsCollateralEv);
  // 可借时，剩余总抵押折合可借数量
  const remainingCollateralAvaiEv =
    canBorrow && gt(totalCollateralAvaiValueRv, 0) && gt(indexPriceRp, 0)
      ? toFixed(Big(totalCollateralAvaiValueRv).div(indexPriceRp).times(valueFactor), valuePrecision2, 0)
      : '0';

  const abEv0 = max(
    0,
    Big(accountBalanceEv).minus(isolatedTotalBalanceEv).minus(totalUsedBalanceEv).minus(frozenAsCollateralEv)
  );
  const abEv = toString(
    Big(availableAccountBalanceEv).plus(remainingCollateralAvaiEv).plus(occupationBalanceEv).plus(relatedCrossUnPnlEv)
  );

  const abEv2 = toString(Big(availableAccountBalanceEv).plus(discountRiskUnPnlEv));
  const availableBalanceEv = gte(abEv, 0) ? abEv : '0';
  const availableBalanceEv2 = gte(abEv2, 0) ? abEv2 : '0';
  const availableBalance = numUtil.floor(availableBalanceEv, availableBalanceValuePrecision, valueScale);
  const availableBalance2 = numUtil.floor(availableBalanceEv, 2, valueScale);
  const availableBalance4 = numUtil.floor(availableBalanceEv, 4, valueScale);

  const positionMarginEv = calcPositionMarginEv(relatedPositions);
  const orderMarginEv = calcOrderMarginEv(relatedPositions);
  const freezeUnrealisedPnlEv = calcFreezeUnrealisedPnlEv(relatedPositions);
  const totalCollateralAvaiEv = canBorrow ? plus(remainingCollateralAvaiEv, occupationBalanceEv) : '0';
  const marginBalanceEv = toString(Big(accountBalanceEv).plus(freezeUnrealisedPnlEv).plus(totalCollateralAvaiEv));
  const equityEv = minus(plus(accountBalanceEv, unrealisedPnlEv), debtEv);
  const availableTransferBalanceEv = min(
    availableBalanceEv2,
    max(Big(accountBalanceEv).minus(bonusBalanceEv).minus(debtEv), 0)
  );

  const availableTransferBalance = numUtil.floor(availableTransferBalanceEv, valuePrecision, valueScale);
  const availableTransferBalance2WU = numUtil.floor(availableTransferBalanceEv, 2, valueScale);

  const availableRepayBalanceEv = max(min(plus(abEv2, debtEv), max(minus(accountBalanceEv, bonusBalanceEv), 0)), 0);

  const posOrdUsedBalanceEv = plus(totalUsedBalanceEv, isolatedTotalBalanceEv);
  const totalUsedBalanceEv2 = toString(Big(posOrdUsedBalanceEv).plus(frozenAsCollateralEv).plus(debtEv));
  const totalEquityEv = plus(availableBalanceEv, totalUsedBalanceEv2);
  return {
    name,
    currency,
    accountBalanceEv,
    accountBalance: numUtil.floor(accountBalanceEv, valuePrecision, valueScale),
    totalUsedBalanceEv: totalUsedBalanceEv2,
    crossAvaiAccountBalanceEv: toString(
      Big(accountBalanceEv).minus(isolatedTotalBalanceEv).minus(debtEv).minus(frozenAsCollateralEv)
    ),
    totalUsedBalance: numUtil.floor(totalUsedBalanceEv2, valuePrecision, valueScale),
    posOrdUsedBalanceEv,
    posOrdUsedBalance: numUtil.floor(posOrdUsedBalanceEv, valuePrecision, valueScale),
    availableBalanceEv,
    availableBalance,
    availableBalanceWU: availableBalance + ' ' + displayCurrency,
    availableBalance2,
    availableBalance2WU: availableBalance2 + ' ' + displayCurrency,
    availableBalance4,
    availableBalance4WU: availableBalance4 + ' ' + displayCurrency,
    availableTransferBalanceEv,
    availableTransferBalance,
    availableTransferBalanceWU: availableTransferBalance + ' ' + displayCurrency,
    availableTransferBalance2WU: availableTransferBalance2WU + ' ' + displayCurrency,
    availableRepayBalanceEv,
    availableRepayBalance: numUtil.floor(availableRepayBalanceEv, valuePrecision, valueScale),
    bonusBalanceEv,
    bonusBalance: numUtil.floor(bonusBalanceEv, valuePrecision, valueScale),
    positionMarginEv,
    positionMargin: numUtil.floor(positionMarginEv, valuePrecision, valueScale),
    unrealisedPnlEv,
    unrealisedPnl: numUtil.floor(unrealisedPnlEv, valuePrecision, valueScale),
    equityEv,
    equity: numUtil.floor(equityEv, valuePrecision, valueScale),
    totalEquityEv,
    totalEquity: numUtil.floor(totalEquityEv, valuePrecision, valueScale),
    marginBalanceEv,
    marginBalance: numUtil.floor(marginBalanceEv, valuePrecision, valueScale),
    orderMarginEv,
    orderMargin: numUtil.floor(orderMarginEv, valuePrecision, valueScale),
    borrowBalanceEv: borrowEv,
    borrowBalance: numUtil.ceil(borrowEv, valuePrecision, valueScale),
    debtBalanceEv: debtEv,
    debtBalance: numUtil.ceil(debtEv, valuePrecision, valueScale),
    interestBalanceEv: interestEv,
    interestBalance: numUtil.ceil(interestEv, valuePrecision, valueScale),
    frozenAsCollateralEv,
    frozenAsCollateral: numUtil.ceil(frozenAsCollateralEv, valuePrecision, valueScale),
    totalCollateralAvaiEv,
    remainingCollateralAvaiEv,
    availableBalanceForAccountEv: abEv0,
    availableBalanceForAccount: numUtil.floor(abEv0, availableBalanceValuePrecision, valueScale),
  };
}
